import { ActionIcon, Badge, Button, Group, Loader, Text, Tooltip } from '@mantine/core';
import { IconMapPin, IconPin } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useContractsQueries } from '../../lib/queries/contracts';
import {
  BatchDetailsProperties,
} from '../../lib/types/batches';

import { fieldsApi } from '@fields/data-access';
import { PfTable } from '@ui';

type CommittedFieldRow = BatchDetailsProperties & { id: number };

interface CommittedFieldsTableProps {
  batchId: number;
  selectedFieldId?: number | null;
  onSelectField?: (fieldId: number) => void;
  onResetSelectedField?: () => void;
}

export const CommittedFieldsTable: FC<CommittedFieldsTableProps> = ({
  batchId,
  selectedFieldId,
  onSelectField,
  onResetSelectedField,
}) => {
  const { t } = useTranslation();
  const contractsQueries = useContractsQueries();

  const batchDetails = useQuery(contractsQueries.batchesDetail(batchId));

  const updateField = useMutation({
    mutationFn: fieldsApi.updateOne,
  });

  const data = useMemo<CommittedFieldRow[]>(() => {
    return (
      batchDetails.data?.lot_fields.features
        .map((feature) => ({
          ...feature.properties,
          id: feature.id as number,
        }))
        .filter((field) =>
          selectedFieldId ? field.id === selectedFieldId : true,
        ) ?? []
    );
  }, [batchDetails.data?.lot_fields.features, selectedFieldId]);

  const speciesOptions = useMemo(() => {
    const species = batchDetails.data?.lot_fields.features
      .map((feature) => feature.properties.species)
      .filter((item) => item !== undefined && item !== null);

    if (!species?.length) {
      return [];
    }

    return Array.from(new Set(species.map((item) => item.id)))
      .map((id) => species.find((item) => item.id === id))
      .filter((item) => item !== undefined)
      .map((item) => ({
        value: item.id.toString(),
        label: item.name,
      }));
  }, [batchDetails.data?.lot_fields.features]);

  const columns = useMemo<MRT_ColumnDef<CommittedFieldRow>[]>(
    () => [
      {
        accessorKey: 'id',
        enableEditing: false,
        grow: false,
        header: t('fields.col.id'),
        minSize: 72,
        size: 88,
      },
      {
        accessorKey: 'name',
        enableEditing: false,
        header: t('fields.col.field-name'),
      },
      {
        accessorKey: 'telepac_code',
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '-',
        enableEditing: false,
        grow: false,
        header: t('contracts.telepac-code'),
        minSize: 100,
        size: 120,
      },
      {
        accessorKey: 'species',
        accessorFn: (row) => row.species?.id.toString(),
        Cell: ({ row }) => row.original.species?.name ?? '-',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        header: t('common.crop'),
        grow: false,
        mantineEditSelectProps: ({ cell }) => ({
          clearable: true,
          data: speciesOptions,
          rightSection:
            updateField.variables?.id === cell.row.original.id &&
            updateField.isPending ? (
              <Loader size="xs" />
            ) : undefined,
          variant: 'default',
        }),
        mantineFilterSelectProps: {
          data: speciesOptions,
        },
        minSize: 120,
        size: 150,
      },
      {
        accessorKey: 'sampling_area',
        Cell: ({ row }) => {
          const forced = row.original.force_sampling ? (
            <Tooltip label={t('contracts.force-sampling')}>
              <span style={{ display: 'inline-flex' }}>
                <IconPin color="var(--mantine-color-orange-6)" size={16} />
              </span>
            </Tooltip>
          ) : null;

          if (row.original.sustainability_status === 'error') {
            return (
              <Group gap={6} wrap="nowrap">
                <Badge color="gray" size="sm" variant="light">
                  {t('fields.sustainability_status.error')}
                </Badge>
                {forced}
              </Group>
            );
          }

          const area =
            row.original.sampling_area == null
              ? '-'
              : `${row.original.sampling_area.toFixed(2)} ha`;

          return (
            <Group gap={6} wrap="nowrap">
              <Text size="sm">{area}</Text>
              {forced}
            </Group>
          );
        },
        enableEditing: false,
        grow: false,
        header: t('fields.col.sampling-area'),
        minSize: 140,
        size: 168,
      },
    ],
    [speciesOptions, t, updateField.isPending, updateField.variables?.id],
  );

  const engagedStats = batchDetails.data?.lot_engaged_fields_stats;

  const tableOptions: MRT_TableOptions<CommittedFieldRow> = {
    columns,
    data,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 40,
      },
    },
    editDisplayMode: 'table',
    enableEditing: true,
    enableFullScreenToggle: false,
    enablePagination: false,
    enableRowVirtualization: true,
    initialState: {
      sorting: [{ desc: false, id: 'name' }],
    },
    mantineEditSelectProps: ({ cell, row }) => ({
      onChange: (value) => {
        if (cell.column.id !== 'species') {
          return;
        }

        updateField.mutate({
          id: row.original.id,
          species:
            value === null || value === undefined || value === ''
              ? null
              : Number(value),
        });
      },
    }),
    mantinePaperProps: {
      h: '100%',
      style: {
        display: 'flex',
        flexDirection: 'column',
      },
    },
    positionActionsColumn: 'first',
    renderBottomToolbar: () => (
      <Group px="md" py="sm">
        {!selectedFieldId ? (
          <Text fw="bold" size="lg">
            {engagedStats?.count} {t('common.parcelles-engagees')} :{' '}
            {(engagedStats?.sampling_area ?? 0).toFixed(2)}{' '}
            {t('common.ha-durables')} ({(engagedStats?.total_area ?? 0).toFixed(2)}{' '}
            ha)
          </Text>
        ) : (
          <Button color="gray" onClick={onResetSelectedField} variant="light">
            {t('common.reinitialiser-les-filtres')}
          </Button>
        )}
      </Group>
    ),
    renderRowActions: ({ row }) => (
      <ActionIcon onClick={() => onSelectField?.(row.original.id)}>
        <IconMapPin style={{ height: '80%', width: '80%' }} />
      </ActionIcon>
    ),
  };

  return (
    <PfTable<CommittedFieldRow>
      enableShowHideColumns={false}
      options={tableOptions}
    />
  );
};
